import { useEffect, useState, useRef } from "react";
import { createIndex, updateIndex } from "../api/agentApi";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Toolbar, 
  Divider, 
  Tooltip, 
  CircularProgress,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from "@mui/material";
import RAGFileUploader from "./RAGFileUploader.tsx";
import StorageIcon from '@mui/icons-material/Storage';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';

export function CodeIndexerPage() {
  const [sourcePath, setSourcePath] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleIndex = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      // Split comma-separated paths, trim, filter out empty strings, sort, and remove duplicates
      const pathArray = Array.from(new Set(sourcePath.split(",").map(p => p.trim()).filter(Boolean))).sort();
      const res = await createIndex({
        name: name || `Code Collection - ${pathArray.join(", ")}`,
        description,
        path: pathArray,
        type: "code"
      });
      setResult(
        <span>
          {res.message || "Processing complete."}
          {res.index_id && (
            <><br /><a href={`/kb/code/${res.index_id}`}>View collection status</a></>
          )}
        </span>
      );
    } catch (e) {
      setError(e.message || "Failed to index codebase.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", alignItems: "center"}}>
        <Typography variant="h4" sx={{ pl: 0 }}>Code Collection</Typography>
      </Toolbar>
      <Divider sx={{ mb: 3 }} />
      <Box component={"form"} sx={{ mx: "auto", py: 4 }}>
        <TextField label="Index Name" value={name} onChange={e => setName(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Source Path(s) (supports comma-deparated list)" value={sourcePath} onChange={e => setSourcePath(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <Button variant="contained" color="primary" onClick={handleIndex} disabled={loading || !sourcePath} sx={{ mb: 2 }}>
          {loading ? "Processing..." : "Collection"}
        </Button>
        {result && <Alert severity="success" sx={{ mt: 2 }}>{result}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    </Box>
  );
}

export function DocumentIndexerPage() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [sourcePathArray, setSourcePathArray] = useState([]);
  const [validationError, setValidationError] = useState({});
  // Track created index to enable updates
  const [createdIndexId, setCreatedIndexId] = useState(null);
  const [indexDetails, setIndexDetails] = useState(null);
  // Track upload ID to reuse for same index
  const [uploadId, setUploadId] = useState(null);
  // Progress tracking
  const [activeStep, setActiveStep] = useState(-1); // -1 means not started
  const [uploadComplete, setUploadComplete] = useState(false);
  const [indexingComplete, setIndexingComplete] = useState(false);
  
  const steps = ["Upload", "Process Files"];
  
  // Auto-index when files are uploaded
  useEffect(() => {
    if (uploadedFiles && Object.keys(uploadedFiles).length) {
      // Mark upload as complete
      setUploadComplete(true);
      setActiveStep(0); // Move to step 0 (Upload Files)
      
      // get source paths for indexing - extract directory paths
      const paths = uploadedFiles?.uploaded?.map((file) => {
        // Extract directory path from file path
        const filePath = file.path;
        const lastSlashIndex = filePath.lastIndexOf('/');
        return lastSlashIndex > 0 ? filePath.substring(0, lastSlashIndex) : filePath;
      });
      // Remove duplicates since multiple files might be in the same directory
      const uniquePaths = Array.from(new Set(paths));
      if (paths && uniquePaths.length > 0) {
        // Accumulate paths instead of replacing them
        setSourcePathArray(prev => {
          const combinedPaths = [...prev, ...uniquePaths];
          return Array.from(new Set(combinedPaths)); // Remove duplicates
        });
        
        // Automatically trigger indexing after upload
        handleIndexAfterUpload(uniquePaths);
      }
    } 
  },[uploadedFiles])

  const handleIndexAfterUpload = async (newPaths) => {
    // Validate name is provided
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationError(errors);
      setError("Collection name is required. Please provide a name before uploading files.");
      setActiveStep(-1);
      setUploadComplete(false);
      return;
    }
    
    setLoading(true);
    setError("");
    setResult(null);
    setActiveStep(1); // Move to step 1 (Index Files)
    setIndexingComplete(false);
    
    try {
      // Combine with existing paths and remove duplicates
      const combinedPaths = [...sourcePathArray, ...newPaths];
      const pathArray = Array.from(new Set(combinedPaths.map(p => p.trim()).filter(Boolean))).sort();
      
      let res;
      if (createdIndexId && indexDetails) {
        // Update existing index
        res = await updateIndex(createdIndexId, {
          source_path: pathArray,
        });
        setResult(
          <span>
            Collection updated successfully! Additional files have been added to the existing collection.
            <br /><a href={`/kb/doc/${createdIndexId}`}>View collection status</a>
          </span>
        );
      } else {
        // Create new index
        res = await createIndex({
          name: name || `Doc Index - ${pathArray.join(", ")}`,
          description,
          path: pathArray,
          type: "doc"
        });
        
        // Store the created index info for future updates
        if (res.index_id) {
          setCreatedIndexId(res.index_id);
          setIndexDetails({
            source_path: pathArray.map(path => ({ path })),
            name: name || `Doc Index - ${pathArray.join(", ")}`,
            type: "doc",
            status: "running"
          });
        }
        
        setResult(
          <span>
            {res.message || "Processing complete."}
            {res.index_id && (
              <><br /><a href={`/kb/doc/${res.index_id}`}>View collection status</a></>
            )}
          </span>
        );
      }
      
      setUploadedFiles({});
      setValidationError({});
      setIndexingComplete(true);
      setActiveStep(2); // Complete - beyond the last step
    } catch (e) {
      setError(e.message || "Failed to process files.");
      setActiveStep(-1);
      setUploadComplete(false);
      setIndexingComplete(false);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    return newErrors;
  };

  const resetToNewIndex = () => {
    setCreatedIndexId(null);
    setIndexDetails(null);
    setUploadId(null); // Reset upload ID for new index
    setResult(null);
    setError("");
    setName("");
    setDescription("");
    setUploadedFiles({});
    setSourcePathArray([]);
    setValidationError({});
    setActiveStep(-1);
    setUploadComplete(false);
    setIndexingComplete(false);
  };

  return (
    <Box sx={{ pt:1, px: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", alignItems: "center"}}>
        <Typography variant="h4" sx={{ pl: 0 }}>Document Collection Builder</Typography>
      </Toolbar>
      
      <Box component={"form"} sx={{ mx: "auto", py: 3 }}>
        <TextField 
          label="Collection Name" 
          value={name} 
          onChange={e => {setName(e.target.value); if (validationError) setValidationError({});}} 
          error={Boolean(validationError.name)}
          helperText={validationError.name}
          fullWidth 
          sx={{ mb: 2 }} 
          disabled={createdIndexId !== null}
        />
        <TextField 
          label="Description (optional)" 
          value={description} 
          onChange={e => setDescription(e.target.value)} 
          fullWidth 
          sx={{ mb: 2 }} 
          disabled={createdIndexId !== null}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 2 }}>
          <InfoOutlinedIcon fontSize="small" color="info" />
          <Typography variant="caption" color="text.secondary">
            {!name.trim() 
              ? "Please provide a collection name before uploading files." 
              : createdIndexId 
                ? "Upload more files to add them to the existing collection." 
                : "Upload files to create a document collection."}
          </Typography>
        </Box>
        
        <RAGFileUploader 
          setUploadedFiles={setUploadedFiles} 
          uploadId={uploadId}
          onUploadIdCreated={setUploadId}
        />
        
        {/* Progress indicator and stepper */}
        {activeStep >= 0 && (
          <Box sx={{ mt: 3 }}>
            {loading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {activeStep === 0 ? "Upload complete, processing..." : "Processing files..."}
                </Typography>
              </Box>
            )}
            
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((label, index) => (
                <Step key={label} expanded={true}>
                  <StepLabel>
                    {label}
                    {index < activeStep && (
                      <CheckCircle color="success" sx={{ ml: 1, fontSize: 20 }} />
                    )}
                  </StepLabel>
                  <StepContent>
                    {index === 0 && uploadComplete && (
                      <Typography variant="body2" color="text.secondary">
                        Files uploaded successfully. Ready to process.
                      </Typography>
                    )}
                    {index === 1 && activeStep > 1 && (
                      <Typography variant="body2" color="text.secondary">
                        {createdIndexId 
                          ? "Files added to existing collection successfully." 
                          : "Collection created and processed successfully."}
                      </Typography>
                    )}
                    {index === 1 && activeStep === 1 && loading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Processing files...
                        </Typography>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}
        
        {result && <Alert severity="success" sx={{ mt: 2 }}>{result}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {createdIndexId && (
          <Alert
            severity={"info"}
            sx={{ mt: 2 }}
            action={
                <Button 
                  color="inherit"
                  size="small"
                  onClick={resetToNewIndex}
                  sx={{ textTransform: 'none' }}
                >
                  Start New Collection Instead
                </Button>
            }
          >
            <Typography variant="body2" color="success.dark">
              ✓ Index "{indexDetails?.name}" created successfully. You can upload more files to add them to this collection.
            </Typography>
          </Alert>
        )}
      </Box>
    </Box>
  );
}
